import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import './Hero.css';
import heroImage from '../../assets/LeasEiZi_Hero-Large.png';
import avatarImage from '../../assets/avatar1.png';

// Animated gradient circle component
function GradientCircle({ className, scrollYProgress, duration = 4 }) {
  // Determine which circle for different effects
  const circleNum = className.includes('circle-1') ? 1
    : className.includes('circle-2') ? 2
    : 3;

  // Subtle parallax movement based on scroll (reduced for centered layout)
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    circleNum === 1 ? [0, 30] : circleNum === 2 ? [0, -20] : [0, -25]
  );

  const x = useTransform(
    scrollYProgress,
    [0, 1],
    circleNum === 1 ? [0, 0] : circleNum === 2 ? [0, -15] : [0, 15]
  );

  // Scale pulsation based on scroll
  const scale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [1, 1.1, 0.95]
  );

  // Opacity fade
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0.3, 0.4, 0.35, 0.2]
  );

  return (
    <motion.div
      className={`gradient-circle ${className}`}
      style={{ y, x, scale, opacity }}
      animate={{
        scale: [1, 1.08, 1],
      }}
      transition={{
        duration: duration,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

// Animated chat bubble in hero
function HeroChatBubble({ isInView }) {
  const bubbleVariants = {
    hidden: {
      opacity: 0,
      scale: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.5,
      }
    }
  };

  const floatAnimation = {
    y: [0, -8, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    }
  };

  return (
    <motion.div
      className="hero-chat-bubble"
      initial="hidden"
      animate={isInView ? ["visible", floatAnimation] : "hidden"}
      variants={bubbleVariants}
      whileHover={{ scale: 1.05 }}
    >
      <img src={avatarImage} alt="hero avatar" className="hero-avatar-image" />
      <p style={{fontSize: "15px"}}>Don't overpay <br /> for rent!</p>
      <motion.a
        href="#get-started"
        className="cta-button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{ display: 'inline-block', textDecoration: 'none' }}
      >
        Get Started
      </motion.a>
    </motion.div>
  );
}

function Hero() {
  const heroRef = useRef(null);
  const isInView = useInView(heroRef, { once: true, amount: 0.2 });

  // Track scroll progress for parallax effects
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  // Title animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.4
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.3
      }
    }
  };

  return (
    <section className="hero" ref={heroRef}>
      {/* Animated gradient circles in triangle formation behind hero image */}
      <div className="gradient-circles-container">
        {/* Blue circle - TOP */}
        <GradientCircle
          className="gradient-circle-1"
          scrollYProgress={scrollYProgress}
          duration={5}
        />
        {/* Light green circle - BOTTOM LEFT */}
        <GradientCircle
          className="gradient-circle-2"
          scrollYProgress={scrollYProgress}
          duration={4}
        />
        {/* Dark green circle - BOTTOM RIGHT */}
        <GradientCircle
          className="gradient-circle-3"
          scrollYProgress={scrollYProgress}
          duration={4.5}
        />
      </div>

      <div className="hero-background">
        <div className="content-wrapper">
          <motion.h1
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={titleVariants}
          >
            Obtain the Apartment Lease Price You Want!
          </motion.h1>

          <motion.p
            className="hero-p"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={subtitleVariants}
          >
            EiZiest Apartment Price Tracking Ever!
          </motion.p>

          <motion.a
            href="#get-started"
            className="cta-button-1"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={buttonVariants}
            whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(139, 197, 63, 0.3)" }}
            whileTap={{ scale: 0.95 }}
          >
            Get started
          </motion.a>

          <motion.img
            src={heroImage}
            alt="Apartment background"
            className="hero-image"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={imageVariants}
          />

          <HeroChatBubble isInView={isInView} />
        </div>
      </div>
    </section>
  );
}

export default Hero;
